// ============ LIVE PRICE ENGINE ============
// Replaces the prototype's MM_LIVE random-walk with a hybrid engine:
//   • Every ~2.5s: a lightweight simulation nudges a handful of symbols so the
//     UI always feels alive (flash up/down, ticker movement).
//   • Every VITE_LIVE_POLL_MS (default 30s): real provider data is pulled and
//     OVERWRITES the simulated values for symbols a provider covers —
//     CoinGecko for BTC/ETH/SOL (always), Finnhub for US equities (if key set).
// Consumers listen to the 'mm-live-tick' window event and read MM_LIVE.last
// for the per-symbol up(+1)/down(-1) flash direction — same contract as before.

import { ASSETS, INDICES } from './data';
import { fetchCryptoQuotes, CRYPTO_SYMBOLS } from './services/coingecko';
import { fetchStockQuotes, fetchIntlQuotes, fetchGoldSpot, fetchMt5Sources, fetchMt5Quotes } from './services/api';
import { subscribeMt5 } from './services/mt5stream';
import { INTL_QUOTE_YAHOO, indexYahoo } from './lib/yahoo';
import { mt5Symbol } from './lib/mt5';

// Symbols served keyless via the /api/yquotes proxy (Yahoo): non-US equities
// (Thai/Asian/EU) plus commodities & macro (gold, oil, US 10Y yield). These
// have explicit Yahoo tickers, so one batched quote call covers them all.
// GOLD is deliberately absent: Yahoo has no spot ticker and its GC=F is the
// December contract, some 1.1% above spot. Gold's stand-in quote comes from
// /api/goldspot instead — see pollGoldSpot.
const YQUOTE_MAP: Record<string, string> = {
  ...INTL_QUOTE_YAHOO,
  WTI: 'CL=F',    // WTI crude futures
  US10Y: '^TNX',  // US 10Y Treasury yield
};
const YQUOTE_FROM_YAHOO: Record<string, string> = Object.fromEntries(
  Object.entries(YQUOTE_MAP).map(([sym, y]) => [y, sym]),
);

// Market indices (ticker tape + dashboard cards) also come from Yahoo — they
// ride along in the same batched /api/yquotes call. Indices without a Yahoo
// ticker (e.g. mai) keep their static value.
// GOLD is excluded for the reason above — its quote comes from /api/goldspot.
// indexYahoo() still answers GC=F for it, which is what the dashboard's hero
// *chart* draws, since no keyless spot series exists: only the quote moves.
const INDEX_FROM_YAHOO: Record<string, string> = Object.fromEntries(
  INDICES.map(ix => [indexYahoo(ix.sym), ix.sym]).filter(([y, s]) => y && s !== 'GOLD') as [string, string][],
);

// Macro gauges the news desk quotes but that no page charts, so they have no
// ASSETS or INDICES row to live in. They ride the same batched call and land in
// MM_LIVE.macro, which is what lets a KPI tile print a real number.
export const MACRO_YAHOO: Record<string, string> = { DXY: 'DX-Y.NYB', VIX: '^VIX' };
const MACRO_FROM_YAHOO: Record<string, string> = Object.fromEntries(
  Object.entries(MACRO_YAHOO).map(([sym, y]) => [y, sym]),
);

/** Window event carrying a pushed headline batch: { items, fresh, at }. */
export const NEWS_EVENT = 'mm-news';
export interface NewsPush { items: any[]; fresh: string[]; at: number; }

interface LiveState {
  last: Record<string, number>;
  /** gauges quoted around the app that nothing charts — see MACRO_YAHOO */
  macro: Record<string, { price: number; chg: number }>;
  /** epoch ms of the last headline batch pushed over the stream (0 = none yet) */
  newsAt: number;
  /** symbols currently fed by a real provider */
  liveSyms: Set<string>;
  simTimer: ReturnType<typeof setInterval> | null;
  pollTimer: ReturnType<typeof setInterval> | null;
  fastTimer: ReturnType<typeof setInterval> | null;
  stream: EventSource | null;
  /** unsubscribe from the shared MT5 push stream */
  mt5Unsub: (() => void) | null;
  start(): void;
  stop(): void;
}

const POLL_MS = Number(import.meta.env.VITE_LIVE_POLL_MS) || 30000;
// Yahoo indices/commodities can't stream (keyless), so poll that one cheap
// batched call more often to keep them near-real-time between the slow poll.
const FAST_MS = Number(import.meta.env.VITE_FAST_POLL_MS) || 3000;

// Daily reference close per symbol (price / (1 + chg%)). Seeded from every REST
// poll; the WebSocket stream carries only price, so we recompute chg from this
// to keep the streamed change % consistent with the provider's official close.
const REF_CLOSE: Record<string, number> = {};
function setRef(sym: string, price: number, chg: number) {
  const ref = price / (1 + chg / 100);
  if (Number.isFinite(ref) && ref > 0) REF_CLOSE[sym] = ref;
}

// US symbols Finnhub can serve (plain US tickers, no exchange-suffix needed)
const FINNHUB_SYMS = ASSETS
  .filter(a => a.market === 'US' && (a.cls === 'Stocks' || a.cls === 'ETF'))
  .map(a => a.sym);

const MT5_FOR_ASSET: Record<string, string> = Object.fromEntries(ASSETS.map(a => [a.sym, mt5Symbol(a)]));
const ASSET_FOR_MT5: Record<string, string> = Object.fromEntries(ASSETS.map(a => [mt5Symbol(a).toUpperCase(), a.sym]));

// ---- MT5 is the primary tape ----
// The user's own terminals are the reference price for everything they quote;
// Yahoo, Finnhub and CoinGecko only fill in what MT5 is not feeding. Two rules
// keep "MT5 first" from turning into a flicker:
//   • One owner per symbol. Four terminals quote the same instrument a little
//     apart — FxPro's US500 prints ~27 points from IC Markets' — so a symbol is
//     pinned to the broker that first fed it and other brokers' ticks for it are
//     dropped. No broker is ranked above another; first to arrive simply keeps
//     it, which is what makes the printed price stop jumping between sources.
//   • Ownership expires. MT5_OWN_MS after the last tick the symbol falls back to
//     Yahoo — that is what "MT5 ดับค่อยใช้ Yahoo" means in practice.
// The EA pushes every fed symbol every 3s whether or not its market is open, so
// a lapsed stamp means the terminal is gone, not that trading is quiet.
const MT5_OWN_MS = POLL_MS * 2 + 15000;  // survives two missed polls when SSE can't run
const MT5_FRESH_MS = 20000;              // a polled quote older than this is a dead terminal's leftover
const mt5Owner: Record<string, { broker: string; ts: number }> = {};

/** True while MT5 is feeding this symbol — every other provider must leave it alone. */
function mt5Owns(sym: string): boolean {
  const o = mt5Owner[sym];
  return !!o && Date.now() - o.ts < MT5_OWN_MS;
}

/** Broker id currently owning this symbol's tape, or null once ownership lapsed.
 *  The dashboard's gold chart reads this so its candles come from the same
 *  terminal whose quote is printed everywhere else — one metal, one source. */
export function mt5OwnerBroker(sym: string): string | null {
  const o = mt5Owner[sym];
  return o && Date.now() - o.ts < MT5_OWN_MS ? o.broker : null;
}

/** Write one MT5 quote onto its asset. Returns false if another broker owns it. */
function applyMt5(sym: string, broker: string, price: number, chg: number, changed: Record<string, number>): boolean {
  if (!(price > 0)) return false;
  const owner = mt5Owner[sym];
  if (owner && owner.broker !== broker && Date.now() - owner.ts < MT5_OWN_MS) return false;
  const a = ASSETS.find(x => x.sym === sym);
  if (!a) return false;
  changed[sym] = price >= a.price ? 1 : -1;
  a.price = price;
  a.chg = chg;
  a.live = true;
  setRef(sym, price, chg);
  MM_LIVE.liveSyms.add(sym);
  mt5Owner[sym] = { broker, ts: Date.now() };
  // GOLD is an ASSETS row and an INDICES row at once (it headlines the dashboard
  // chart). Moving both together stops the same metal printing two prices.
  const ix = INDICES.find(x => x.sym === sym);
  if (ix) { ix.price = price; ix.chg = chg; }
  return true;
}

function emit(changed: Record<string, number>) {
  MM_LIVE.last = changed;
  window.dispatchEvent(new CustomEvent('mm-live-tick'));
}

// ---- Simulation tick (keeps everything feeling live between real polls) ----
function simTick() {
  const changed: Record<string, number> = {};
  const n = 5 + Math.floor(Math.random() * 6);
  for (let i = 0; i < n; i++) {
    const a = ASSETS[Math.floor(Math.random() * ASSETS.length)];
    // Don't fight a real provider value with noise — skip live-fed symbols.
    if (MM_LIVE.liveSyms.has(a.sym)) continue;
    const drift = (Math.random() - 0.5) * 2 * (a.vol || 0.015) * 0.18;
    const np = a.price * (1 + drift);
    changed[a.sym] = np >= a.price ? 1 : -1;
    a.chg = +(a.chg + drift * 100).toFixed(2);
    a.price = np;
  }
  INDICES.forEach(ix => {
    if (Math.random() < 0.45) return;
    const drift = (Math.random() - 0.5) * 0.0009;
    ix.price = ix.price * (1 + drift);
    ix.chg = +(ix.chg + drift * 100).toFixed(2);
    changed[ix.sym] = drift >= 0 ? 1 : -1;
  });
  emit(changed);
}

// ---- Baseline poll: crypto (CoinGecko) + US equities (Finnhub) + MT5 ----
// These carry the official day change % and seed the streaming reference close.
// US + crypto also ride the WebSocket stream, which supplies the tick-by-tick
// smoothness on top of this slower baseline.
async function pollBaseline() {
  const changed: Record<string, number> = {};

  const crypto = await fetchCryptoQuotes();
  for (const sym of CRYPTO_SYMBOLS) {
    const q = crypto[sym];
    if (!q || mt5Owns(sym)) continue;
    const a = ASSETS.find(x => x.sym === sym);
    if (!a) continue;
    changed[sym] = q.price >= a.price ? 1 : -1;
    a.price = q.price;
    a.chg = q.chg;
    a.live = true;
    setRef(sym, q.price, q.chg);
    MM_LIVE.liveSyms.add(sym);
  }

  // US equities/ETFs via the /api proxy (Finnhub). Returns {} when no key set.
  const stocks = await fetchStockQuotes(FINNHUB_SYMS);
  for (const [sym, q] of Object.entries(stocks)) {
    if (mt5Owns(sym)) continue;
    const a = ASSETS.find(x => x.sym === sym);
    if (!a) continue;
    changed[sym] = q.price >= a.price ? 1 : -1;
    a.price = q.price;
    a.chg = q.chg;
    a.live = true;
    setRef(sym, q.price, q.chg);
    MM_LIVE.liveSyms.add(sym);
  }

  // MT5 last, so it overwrites whatever the two above just wrote for a symbol it
  // also carries. This is the slow leg — startMt5Stream() below is what keeps an
  // owned symbol moving tick by tick between these polls.
  try {
    const online = (await fetchMt5Sources()).filter(s => s.online);
    const filled = new Set<string>();
    for (const src of online) {
      const want = ASSETS.filter(a => !filled.has(a.sym)).map(a => MT5_FOR_ASSET[a.sym]);
      if (!want.length) break;
      const quotes = await fetchMt5Quotes(src.id, want);
      for (const [ticker, q] of Object.entries(quotes)) {
        const sym = ASSET_FOR_MT5[ticker.toUpperCase()];
        if (!sym || filled.has(sym)) continue;
        // The server serves a quote for 90s after the terminal stops pushing.
        // Taking one of those would keep Yahoo locked out of a symbol nobody is
        // feeding any more, so a stale quote is left where it is.
        if (q.ageMs >= MT5_FRESH_MS) continue;
        if (applyMt5(sym, src.id, q.price, q.chg, changed)) filled.add(sym);
      }
    }
  } catch (e) { void e; }

  if (Object.keys(changed).length) emit(changed);
}

// ---- Fast poll: Thai/Asian/EU equities + gold/oil/US10Y + market indices via
// the /api proxy (Yahoo, keyless) — one batched request covers them all. These
// have no free real-time stream, so a frequent cheap poll keeps them lively.
async function pollYahoo() {
  const changed: Record<string, number> = {};
  const intl = await fetchIntlQuotes([
    ...Object.values(YQUOTE_MAP), ...Object.keys(INDEX_FROM_YAHOO), ...Object.keys(MACRO_FROM_YAHOO),
  ]);
  for (const [ysym, q] of Object.entries(intl)) {
    // This is the fastest poll in the engine (3s), so without the mt5Owns guards
    // below it would overwrite every MT5 price within three seconds of the 30s
    // baseline poll writing it — MT5 would be "primary" in name only.
    const sym = YQUOTE_FROM_YAHOO[ysym];
    const a = sym && !mt5Owns(sym) && ASSETS.find(x => x.sym === sym);
    if (a) {
      changed[sym] = q.price >= a.price ? 1 : -1;
      a.price = q.price;
      a.chg = q.chg;
      a.live = true;
      setRef(sym, q.price, q.chg);
      MM_LIVE.liveSyms.add(sym);
    }
    // No early exit: one Yahoo ticker can back both an asset and an index row —
    // GC=F is the GOLD asset and the gold row on the dashboard. Stopping at the
    // asset left the index showing its seed price for ever.
    const ixSym = INDEX_FROM_YAHOO[ysym];
    const ix = ixSym && !mt5Owns(ixSym) && INDICES.find(x => x.sym === ixSym);
    if (ix) {
      changed[ixSym] = q.price >= ix.price ? 1 : -1;
      ix.price = q.price;
      ix.chg = q.chg;
      setRef(ixSym, q.price, q.chg);
      MM_LIVE.liveSyms.add(ixSym);
    }
    const mSym = MACRO_FROM_YAHOO[ysym];
    if (mSym) {
      const prev = MM_LIVE.macro[mSym];
      changed[mSym] = !prev || q.price >= prev.price ? 1 : -1;
      MM_LIVE.macro[mSym] = { price: q.price, chg: q.chg };
      MM_LIVE.liveSyms.add(mSym);
    }
  }
  if (Object.keys(changed).length) emit(changed);
}

// ---- Gold's stand-in, for when no terminal is running ----
// Rides the fast timer alongside pollYahoo, but on its own upstream, because
// Yahoo cannot quote spot gold at all: the only gold it serves is GC=F, the
// December contract, which sat $47 above the four brokers' spot on 5 Aug 2026.
// GOLD is both an ASSETS row and an INDICES row, and both are written here so
// the metal prints one price wherever it appears.
async function pollGoldSpot() {
  if (mt5Owns('GOLD')) return;   // a terminal is quoting it — that is the real thing
  const q = await fetchGoldSpot();
  if (!q || !(q.price > 0)) return;
  const a = ASSETS.find(x => x.sym === 'GOLD');
  if (!a) return;
  const dir = q.price >= a.price ? 1 : -1;
  a.price = q.price;
  a.chg = q.chg;
  a.live = true;
  setRef('GOLD', q.price, q.chg);
  MM_LIVE.liveSyms.add('GOLD');
  const ix = INDICES.find(x => x.sym === 'GOLD');
  if (ix) { ix.price = q.price; ix.chg = q.chg; }
  emit({ GOLD: dir });
}

// ---- Real-time stream: Finnhub trades fanned out from the server over SSE.
// Each message is a batch of { sym, price }; we recompute chg% from REF_CLOSE
// so the streamed change stays consistent with the provider's official close.
function applyTrade(batch: { sym: string; price: number }[]) {
  const changed: Record<string, number> = {};
  for (const { sym, price } of batch) {
    if (!(typeof price === 'number' && price > 0)) continue;
    if (mt5Owns(sym)) continue;   // the terminal's tape wins over Finnhub's
    const a = ASSETS.find(x => x.sym === sym);
    if (!a) continue;
    changed[sym] = price >= a.price ? 1 : -1;
    a.price = price;
    const ref = REF_CLOSE[sym];
    if (ref) a.chg = +((price / ref - 1) * 100).toFixed(2);
    a.live = true;
    MM_LIVE.liveSyms.add(sym);
  }
  if (Object.keys(changed).length) emit(changed);
}

// A refused reconnect (the API restarting behind Cloudflare answers 502) closes
// an EventSource for good, and this one carries the real-time trades AND the
// news push - so after each server restart every open tab quietly lost both
// until it was reloaded. A closed stream is rebuilt, backing off to a minute.
let streamRetryT: ReturnType<typeof setTimeout> | null = null;
let streamRetryMs = 2000;
function startStream() {
  if (typeof EventSource === 'undefined' || MM_LIVE.stream || streamRetryT) return;
  const base = (import.meta.env.VITE_API_BASE as string | undefined) || '';
  let es: EventSource;
  try { es = new EventSource(`${base}/api/stream`); } catch { return; }
  es.addEventListener('open', () => { streamRetryMs = 2000; });
  es.addEventListener('error', () => {
    if (es.readyState !== 2 || MM_LIVE.stream !== es) return;
    MM_LIVE.stream = null;
    streamRetryT = setTimeout(() => { streamRetryT = null; if (MM_LIVE.pollTimer) startStream(); }, streamRetryMs);
    streamRetryMs = Math.min(60000, streamRetryMs * 2);
  });
  es.addEventListener('trade', (e: MessageEvent) => {
    try { applyTrade(JSON.parse(e.data)); } catch { /* ignore malformed frame */ }
  });
  // Headlines ride the same socket (see server/stream.mjs). This module owns the
  // connection, so it re-broadcasts them as a window event and useNews picks
  // them up — one stream, no second EventSource per page.
  es.addEventListener('news', (e: MessageEvent) => {
    try {
      const d = JSON.parse(e.data);
      if (Array.isArray(d?.items) && d.items.length) {
        MM_LIVE.newsAt = d.at || Date.now();
        window.dispatchEvent(new CustomEvent(NEWS_EVENT, { detail: d }));
      }
    } catch { /* ignore malformed frame */ }
  });
  // Transient drops reconnect by themselves; a refused reconnect is rebuilt by
  // the error handler above.
  MM_LIVE.stream = es;
}

// ---- MT5 push: the primary tape's real-time leg ----
// pollBaseline only asks every POLL_MS, while the terminals push every 3s and
// the server relays exactly the app's assets down /api/mt5/stream. Riding that
// stream is what lets MT5 displace Yahoo without slowing the price down — and
// it is also what keeps ownership stamped between the slow polls.
function startMt5Stream() {
  if (MM_LIVE.mt5Unsub) return;
  MM_LIVE.mt5Unsub = subscribeMt5(e => {
    if (e.type !== 'quotes') return;
    const changed: Record<string, number> = {};
    for (const q of e.quotes) {
      const sym = ASSET_FOR_MT5[String(q.sym).toUpperCase()];
      if (sym) applyMt5(sym, e.broker, q.price, q.chg, changed);
    }
    if (Object.keys(changed).length) emit(changed);
  });
}

export const MM_LIVE: LiveState = {
  last: {},
  macro: {},
  newsAt: 0,
  liveSyms: new Set<string>(),
  simTimer: null,
  pollTimer: null,
  fastTimer: null,
  stream: null,
  mt5Unsub: null,
  start() {
    if (this.pollTimer) return;
    pollBaseline();       // seed crypto/US/MT5 + reference closes
    pollYahoo();          // seed indices/commodities
    pollGoldSpot();       // gold, which Yahoo cannot quote
    startStream();        // real-time trades on top (US + crypto)
    startMt5Stream();     // the terminals' own tape, which outranks both
    this.pollTimer = setInterval(pollBaseline, POLL_MS);
    this.fastTimer = setInterval(() => { pollYahoo(); pollGoldSpot(); }, FAST_MS);
  },
  stop() {
    if (this.simTimer) { clearInterval(this.simTimer); this.simTimer = null; }
    if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
    if (this.fastTimer) { clearInterval(this.fastTimer); this.fastTimer = null; }
    if (this.stream) { this.stream.close(); this.stream = null; }
    if (streamRetryT) { clearTimeout(streamRetryT); streamRetryT = null; }
    if (this.mt5Unsub) { this.mt5Unsub(); this.mt5Unsub = null; }
  },
};

// A few prototype components still read window.MM_LIVE.last directly.
declare global {
  interface Window { MM_LIVE: LiveState; }
}
if (typeof window !== 'undefined') window.MM_LIVE = MM_LIVE;
